import { Injectable, ApplicationRef, ComponentRef, createComponent, EnvironmentInjector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

import { ToastComponent } from '@components/toast/toast.component';

@Injectable({
    providedIn: 'root'
})
export class Service {
    private Translation: any = {};

    constructor(
        private ApplicationRef: ApplicationRef,
        private EnvironmentInjector: EnvironmentInjector,
        private TranslateService: TranslateService,
    ) {
        this.loadTranslations();

        this.TranslateService.onLangChange.subscribe(() => {
            this.loadTranslations();
        });
    }

    private async loadTranslations() {
        try {
            this.Translation = await firstValueFrom(this.TranslateService.get(['SUCCESS', 'ERRORS', 'BUTTONS', 'LIST', 'COMMON', 'NOTIFICATION']));
        }

        catch (error) {
            console.error('Error to load translate dictionaries', error);
        }
    }

    private Translate(key: string): string {
        if (!this.Translation || typeof this.Translation !== 'object') {
            return key;
        }

        const parts = key.split('.');
        let translate = this.Translation;

        for (const part of parts) {
            if (translate[part] == null) {
                return key;
            }

            translate = translate[part];
        }

        return translate;
    }

    private async translateValue(key?: string, params?: any, fallback?: string): Promise<string> {
        if (!key) return fallback ?? '';

        try {
            const translated = await firstValueFrom(
                this.TranslateService.get(key, params ?? {})
            );

            if (translated === key && fallback) {
                return fallback;
            }

            return translated;
        }

        catch {
            return fallback ?? '';
        }
    }

    alert(data: any) {
        Swal.fire({
            icon: data.icon ?? 'error',
            timer: data.timer ?? 0,
            allowEscapeKey: false,
            allowOutsideClick: false,
            buttonsStyling: false,

            title: data.title ?? '',
            text: data.text ?? '',
            html: data.html ?? '',
            footer: data.footer ?? '',
            timerProgressBar: data.timer ? true : false,
            confirmButtonText: data.text_button_ok ?? this.Translate('BUTTONS.OK'),

            customClass: {
                confirmButton: 'btn bg-gradient-primary',
                icon: 'swal-icon-custom-error'
            }
        });
    }

    alertAction(data: any): Promise<string | null> {
        return Swal.fire({
            icon: data.icon ?? 'success',

            title: data.title ?? '',
            text: data.text ?? '',
            html: data.html ?? '',

            allowEscapeKey: false,
            allowOutsideClick: false,
            buttonsStyling: false,

            showConfirmButton: data.showConfirmButton ?? false,
            showCancelButton: data.showCancelButton ?? false,
            showDenyButton: data.showDenyButton ?? false,

            confirmButtonText: data.confirmText ?? 'Action 1',
            denyButtonText: data.denyText ?? 'Action 2',
            cancelButtonText: data.cancelText ?? 'Cancel',

            customClass: {
                actions: 'swal-actions-gap',
                confirmButton: 'btn bg-gradient-primary',
                denyButton: 'btn bg-gradient-success',
                cancelButton: 'btn bg-gradient-danger'
            }

        }).then(result => {
            if (result.isConfirmed){ 
                return 'confirm';
            }
            
            if (result.isDenied) {
                return 'deny';
            }
            
            if (result.dismiss === Swal.DismissReason.cancel) {
                return 'cancel';
            }

            return null;
        });
    }

    image(data: any) {
        const imageUrl = data.baseUrl + data.image;

        Swal.fire({
            allowEscapeKey: true,
            allowOutsideClick: true,
            buttonsStyling: true,
            showConfirmButton: false,
            width: '80vw',
            padding: '2rem',
            title: data.title ?? '',
            html: data.image ? `
                <div>
                    <div>
                        <img src="${imageUrl}" alt="${data.alt ?? ''}" style="max-width:100%;">
                    </div>
                    ${data.text ? `<p>${data.text}</p>` : ''}
                    ${data.footer ? `<div>${data.footer}</div>` : ''}
                </div>
            ` : '',
            timerProgressBar: !!data.timer
        });
    }

    confirm(data: any): Promise<boolean> {
        return Swal.fire({
            icon: data.icon ?? 'warning',
            focusConfirm: false,
            showCancelButton: true,
            buttonsStyling: false,
            allowEscapeKey: false,
            allowOutsideClick: false,

            title: data.title,
            html: data.html,
            text: data.text,
            footer: data.footer,

            confirmButtonText: data.confirmText ?? this.Translate('BUTTONS.CONFIRM'),
            cancelButtonText: data.cancelText ?? this.Translate('BUTTONS.CANCEL'),

            customClass: {
                actions: 'swal-actions-gap',
                confirmButton: 'btn bg-gradient-primary',
                cancelButton: 'btn bg-gradient-danger',
                icon: 'swal-icon-custom-warning'
            }

        }).then(result => result.isConfirmed);
    }

    simpleToast(data: any) {
        const Toast = Swal.mixin({
            toast: true,
            timer: data.timer ?? 5000,
            showConfirmButton: false,
            position: data.position ?? 'top-end',
            timerProgressBar: data.timerProgressBar ?? true,

            didOpen: toast => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });

        Toast.fire({
            icon: data.icon ?? 'success',
            title: data.message ?? ''
        });
    }

    toast(data: any) {
        const component_ref: ComponentRef<ToastComponent> = createComponent(ToastComponent, {
            environmentInjector: this.EnvironmentInjector
        });

        component_ref.instance.time = data.time ?? this.Translation.NOTIFICATION.JUST_NOW;
        component_ref.instance.position = data.position ?? 'top-right';
        component_ref.instance.type = data.type ?? 'success';
        component_ref.instance.title = data.title ?? '';
        component_ref.instance.message = data.message ?? '';
        component_ref.instance.url = data.url ?? null;
        component_ref.instance.url_target = data.url_target ?? '_self';
        component_ref.instance.url_query = data.url_query ?? null;
        component_ref.instance.onClick = data.onClick ?? null;

        this.ApplicationRef.attachView(component_ref.hostView);

        document.body.appendChild(component_ref.location.nativeElement);

        component_ref.instance.show();

        setTimeout(() => {
            component_ref.destroy();
        }, data.timer ?? 5000);
    }
}